/**
 * Video Cleanup Modal Component
 * 
 * Simple modal following the same pattern as VideoModal
 */

import React, { useState } from 'react';
import styles from '../Modal/Modal.module.css';
import { X } from 'lucide-react';
import { useAuth } from '../../context/Auth';
import { useTranslation } from 'react-i18next';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface CleanupConfig {
  projectId: string;
  contentType: 'podcast' | 'vlog' | 'interview' | 'tutorial' | 'presentation';
  language: string;
  provider: 'auto' | 'claude' | 'gemini';
  skipCaption?: boolean; // Flag to reuse existing captions
}

interface CleanupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartCleanup: (config: CleanupConfig) => void;
  projectId?: string;
  hasCaption?: boolean; // Whether project has existing captions
}

// ============================================================================
// Constants
// ============================================================================

const CONTENT_TYPES = [
  { value: 'podcast', label: '🎙️ Podcast' },
  { value: 'vlog', label: '📹 Vlog' },
  { value: 'interview', label: '🎤 Interview' },
  { value: 'tutorial', label: '📚 Tutorial' },
  { value: 'presentation', label: '💼 Presentation' },
];

const LANGUAGES = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
];

const AI_PROVIDERS = [
  { value: 'auto', label: '✨ Auto (Best)' },
  { value: 'claude', label: '🤖 Claude' },
  { value: 'gemini', label: '💎 Gemini' },
];

// ============================================================================
// Component
// ============================================================================

const CleanupModal: React.FC<CleanupModalProps> = ({
  isOpen,
  onClose,
  onStartCleanup,
  projectId,
  hasCaption = false,
}) => {
  const { t } = useTranslation('videoEditor');
  const { user } = useAuth();
  const [contentType, setContentType] = useState<string>('podcast');
  const [language, setLanguage] = useState<string>('auto');
  const [provider, setProvider] = useState<string>('claude'); // Default to Claude

  // Check if user is admin
  const isAdmin = user?.role === 'admin';
  
  // Content types with translations
  const CONTENT_TYPES = [
    { value: 'podcast', label: `🎙️ ${t('videoEditor.cleanup.contentType.podcast')}` },
    { value: 'vlog', label: `📹 ${t('videoEditor.cleanup.contentType.vlog')}` },
    { value: 'interview', label: `🎤 ${t('videoEditor.cleanup.contentType.interview')}` },
    { value: 'tutorial', label: `📚 ${t('videoEditor.cleanup.contentType.tutorial')}` },
    { value: 'presentation', label: `💼 ${t('videoEditor.cleanup.contentType.presentation')}` },
  ];

  const LANGUAGES = [
    { value: 'auto', label: t('videoEditor.cleanup.language.auto') },
    { value: 'en', label: t('videoEditor.cleanup.language.en') },
    { value: 'es', label: t('videoEditor.cleanup.language.es') },
    { value: 'fr', label: t('videoEditor.cleanup.language.fr') },
    { value: 'de', label: t('videoEditor.cleanup.language.de') },
    { value: 'it', label: t('videoEditor.cleanup.language.it') },
    { value: 'pt', label: t('videoEditor.cleanup.language.pt') },
    { value: 'zh', label: t('videoEditor.cleanup.language.zh') },
    { value: 'ja', label: t('videoEditor.cleanup.language.ja') },
    { value: 'ko', label: t('videoEditor.cleanup.language.ko') },
  ];

  const AI_PROVIDERS = [
    { value: 'auto', label: `✨ ${t('videoEditor.cleanup.provider.auto')}` },
    { value: 'claude', label: `🤖 ${t('videoEditor.cleanup.provider.claude')}` },
    { value: 'gemini', label: `💎 ${t('videoEditor.cleanup.provider.gemini')}` },
  ];

  const handleStartCleanup = (skipCaption: boolean = false) => {
    onStartCleanup({
      projectId: projectId || '',
      contentType: contentType as CleanupConfig['contentType'],
      language,
      provider: provider as CleanupConfig['provider'],
      skipCaption,
    });
  };

  const handleClose = () => {
    // Reset to defaults
    setContentType('podcast');
    setLanguage('auto');
    setProvider('claude'); // Reset to Claude default
    onClose();
  };

  return (
    <div className={`${styles.modalOverlay} ${isOpen ? styles.open : ''}`} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={handleClose} aria-label="Close Modal">
          <X size={24} />
        </button>
        <h2 className={styles.modalTitle}>{t('videoEditor.cleanup.modalTitle')}</h2>
        
        <div className={styles.modalBody}>
          {/* Info Banner */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1))',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            color: '#e0e0e0',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            {t('videoEditor.cleanup.description')}
          </div>

          {/* Clean Again Info Banner (shown when captions exist) */}
          {hasCaption && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(16, 185, 129, 0.1))',
              border: '1px solid rgba(34, 197, 94, 0.2)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              color: '#e0e0e0',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              {t('videoEditor.cleanup.cleanAgainInfo')}
            </div>
          )}

          {/* Content Type Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label className={styles.label}>
              {t('videoEditor.cleanup.contentType.label')} <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              className={styles.input}
              style={{ cursor: 'pointer' }}
            >
              {CONTENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <small style={{ color: '#888', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Helps AI understand context for better results
            </small>
          </div>

          {/* Language Selection - Only show if not reusing captions */}
          {!hasCaption && (
            <div style={{ marginBottom: '20px' }}>
              <label className={styles.label}>{t('videoEditor.cleanup.language.label')}</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className={styles.input}
                style={{ cursor: 'pointer' }}
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <small style={{ color: '#888', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                Auto-detect works for most videos
              </small>
            </div>
          )}

          {/* AI Provider Selection - Admin Only */}
          {isAdmin && (
            <div style={{ marginBottom: '20px' }}>
              <label className={styles.label}>
                {t('videoEditor.cleanup.provider.label')} 
                <span style={{ color: '#888', marginLeft: '6px', fontSize: '12px' }}>
                  {t('videoEditor.cleanup.provider.adminOnly')}
                </span>
              </label>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className={styles.input}
                style={{ cursor: 'pointer' }}
              >
                {AI_PROVIDERS.map(prov => (
                  <option key={prov.value} value={prov.value}>
                    {prov.label}
                  </option>
                ))}
              </select>
              <small style={{ color: '#888', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                Auto selects the best AI for your content
              </small>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className={styles.modalFooter}>
          <button
            className={styles.buttonSecondary}
            onClick={handleClose}
          >
            {t('videoEditor.cleanup.buttons.cancel')}
          </button>
          
          {/* Show both buttons when captions exist */}
          {hasCaption ? (
            <>
              <button
                className={styles.button}
                onClick={() => handleStartCleanup(false)}
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  border: 'none',
                  color: 'white'
                }}
              >
                {t('videoEditor.cleanup.buttons.newCaption')}
              </button>
              <button
                className={styles.button}
                onClick={() => handleStartCleanup(true)}
                style={{
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  color: 'white'
                }}
              >
                {t('videoEditor.cleanup.buttons.reuseCaption')}
              </button>
            </>
          ) : (
            <button
              className={styles.button}
              onClick={() => handleStartCleanup(false)}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                border: 'none',
                color: 'white'
              }}
            >
              {t('videoEditor.cleanup.buttons.startCleanup')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CleanupModal;
